import {
    applicationRepository,
    eventRepository,
    seasonRepository
} from "../storage/storage";
import ApplicationConfig from "../sports/common/application-config";
import { isArray } from "./common/object-util";
import GamedayApiService from "./gameday-api-service";
import SecurityService from "./security-service";
import SeasonService from "./season-service";
import ContextService from "./context-service";
import GameService from "./game-service";
import PlayerService from "./player-service";
import EventStatus from "../sports/common/event-status";

class ApplicationService {
    async setValue(key, value) {
        let appConfig = await applicationRepository.getAll().first();
        if (!appConfig) {
            appConfig = new ApplicationConfig();
        }

        appConfig[key] = value;

        return applicationRepository.put(appConfig);
    }

    async getValue(key) {
        const appConfig = await applicationRepository.getAll().first();

        return appConfig ? appConfig[key] : null;
    }

    async setCurrentSeasonId(seasonId) {
        return this.setValue("currentSeasonId", seasonId);
    }

    async getCurrentSeasonId() {
        return this.getValue("currentSeasonId");
    }

    async setCurrentEventId(eventId) {
        return this.setValue("currentEventId", eventId);
    }

    async getCurrentEventId() {
        return this.getValue("currentEventId");
    }

    async setSelectedSport(selectedSport) {
        let value = selectedSport;
        if (value && !isArray(value)) {
            value = [value];
        }

        return this.setValue("selectedSport", value);
    }

    async getSelectedSport() {
        return this.getValue("selectedSport");
    }

    async setTimeZones(timeZones) {
        return this.setValue("timeZones", timeZones);
    }

    async getTimeZones() {
        return this.getValue("timeZones");
    }

    async setSelectedDate(date) {
        return this.setValue("selectedDate", date);
    }

    async getSelectedDate() {
        return this.getValue("selectedDate");
    }

    async setAllowEventSettingsEditing(value) {
        return this.setValue("allowEventSettingsEditing", value);
    }

    async getAllowEventSettingsEditing() {
        return this.getValue("allowEventSettingsEditing");
    }

    async setApiAccess(value) {
        return this.setValue("apiAccess", value);
    }

    async getSelectedSportOrDefault(sportAccess, currentEvent) {
        let selectedSports = await this.getSelectedSport();
        if (!selectedSports && currentEvent) {
            selectedSports = [currentEvent.sportId];
        }

        let authorizedSports =
            selectedSports?.filter((sport) =>
                sportAccess.some((code) => code.value === sport)
            ) || [];

        if (!authorizedSports.length && sportAccess?.length === 1) {
            authorizedSports = [sportAccess[0].value];
        }

        if (
            authorizedSports.some(
                (rs) => !selectedSports.find((ss) => ss === rs)
            )
        ) {
            await ApplicationService.setSelectedSport(authorizedSports);
        }

        return authorizedSports;
    }

    async getSelectedDateOrDefault(currentEvent) {
        let selectedDate = await this.getSelectedDate();
        const startDateTime = currentEvent?.startDateTime;
        if (!selectedDate && currentEvent) {
            selectedDate = startDateTime;
        }

        return selectedDate || new Date();
    }

    async getAppData() {
        const sportAccess = await SecurityService.getSportAccess();
        const currentEvent = await this.getCurrentEvent();
        const [
            timeZones,
            selectedDate,
            selectedSport,
            allowEventSettingsEditing
        ] = await Promise.all([
            GamedayApiService.getTimeZones(),
            this.getSelectedDateOrDefault(currentEvent),
            this.getSelectedSportOrDefault(sportAccess, currentEvent),
            this.getAllowEventSettingsEditing()
        ]);

        return {
            timeZones,
            sportAccess,
            selectedSport,
            selectedDate,
            allowEventSettingsEditing
        };
    }

    async getCurrentSeason() {
        const currentSeasonId = await this.getCurrentSeasonId();

        if (currentSeasonId) {
            return await seasonRepository.getById(currentSeasonId);
        }

        return null;
    }

    async setCurrentSeason(seasonId) {
        await this.setCurrentSeasonId(seasonId);

        return seasonRepository.getById(seasonId);
    }

    async setCurrentSeasonByExternalId(externalSeasonId) {
        let season = await SeasonService.getBySeasonId(externalSeasonId);
        if (!season) {
            season = await GamedayApiService.getSeason(externalSeasonId);
        }

        return this.setCurrentSeason(season.id);
    }

    async getCurrentEvent() {
        const currentEventId = await this.getCurrentEventId();

        if (currentEventId) {
            return await eventRepository.getById(currentEventId);
        }

        return null;
    }

    async setCurrentEvent(eventId) {
        const event = await eventRepository.getById(eventId);
        await this.setCurrentEventId(eventId);

        if (EventStatus.isFinal(event)) {
            await ContextService.updateContextScore(event);
        }

        const eventTeams = await this.getSeasonTeams(event);
        const game = await GameService.getGameByEventId(event.id);
        const gameContext = await ContextService.getGameContext(game.id);

        return { event, eventTeams, gameContext };
    }

    async getSeasonTeams(event) {
        const { teams, seasonId, eventId } = event;
        const { awayTeam, homeTeam } = teams;
        const teamIds = [awayTeam, homeTeam].map((t) => t.teamId);

        // Pre players sync
        let currentPlayers = {};
        const game = await GameService.getGameByEventId(event.id);
        if (game) {
            currentPlayers = {
                [awayTeam.id]: await PlayerService.getPlayers(awayTeam.id),
                [homeTeam.id]: await PlayerService.getPlayers(homeTeam.id)
            };
        }
        //

        const eventTeams = await GamedayApiService.getSeasonTeams(
            seasonId,
            teamIds
        );

        await Promise.all(
            eventTeams.map((team) =>
                GamedayApiService.syncTeamPlayers(team, eventId)
            )
        );

        //TODO: Why can't tis go before the above sync. It causes duplicated key on teamId
        eventTeams.forEach(
            (t) =>
                (t.id =
                    t.teamId === awayTeam.teamId ? awayTeam.id : homeTeam.id)
        );
        //

        // Post players sync
        if (game) {
            // NOTE: Cannot be paralleled cause the game context is shared and this method changes it.
            // Otherwise, it happens that the parallel calls modifies the team contexts without waiting for the other to complete.
            // Think about making this "thread safe"-ish
            await this.togglePlayerDefaults(
                game.id,
                awayTeam.id,
                currentPlayers[awayTeam.id]
            );
            await this.togglePlayerDefaults(
                game.id,
                homeTeam.id,
                currentPlayers[homeTeam.id]
            );
        }

        return eventTeams;
    }

    async togglePlayerDefaults(gameId, teamId, currentPlayers) {
        if (gameId) {
            const savedPlayers = await PlayerService.getPlayers(teamId);
            const newPlayers = savedPlayers.filter(
                (sp) => !currentPlayers.find((lp) => lp.id === sp.id)
            );

            if (newPlayers.length) {
                await ContextService.toggleDefaults(
                    gameId,
                    teamId,
                    newPlayers.map((p) => p.id)
                );
            }
        }
    }
}

export default new ApplicationService();
